import type { OccurrenceDto } from '@familynest/shared'
import { CalendarDays } from 'lucide-react'
import { formatDayLong, formatTime, isSameDay, startOfDay, weekDays } from '../../lib/date'
import { groupByDaySpanning, isMultiDay, localDayKey } from './utils'

/**
 * Agenda-Ansicht für Woche (7 Spalten Mo–So) und Tag (eine Spalte). Alternative
 * zum Zeitraster (TimeGridView, ADR-088) — auf Handys der Standard.
 */
export function AgendaView({
  mode,
  cursor,
  occurrences,
  colorFor,
  onDayClick,
  onEventClick,
}: {
  mode: 'week' | 'day'
  cursor: Date
  occurrences: OccurrenceDto[]
  colorFor: (occ: OccurrenceDto) => string
  onDayClick: (day: Date) => void
  onEventClick: (occ: OccurrenceDto) => void
}): React.JSX.Element {
  const days = mode === 'week' ? weekDays(cursor) : [startOfDay(cursor)]
  const byDay = groupByDaySpanning(occurrences, days)
  const today = new Date()

  return (
    <div className={`grid h-full gap-2 overflow-auto ${mode === 'week' ? 'grid-cols-1 md:grid-cols-7' : 'grid-cols-1'}`}>
      {days.map((day) => {
        const items = byDay.get(localDayKey(day)) ?? []
        const isToday = isSameDay(day, today)
        return (
          <div
            key={day.toISOString()}
            className="flex min-h-40 flex-col rounded-[var(--radius-card)] border"
            style={{ backgroundColor: 'var(--color-card)', borderColor: isToday ? 'var(--color-accent)' : 'var(--color-line)' }}
          >
            <button
              onClick={() => onDayClick(day)}
              className="border-b px-3 py-2 text-left text-sm font-medium hover:brightness-95"
              style={{ borderColor: 'var(--color-line)', color: isToday ? 'var(--color-accent)' : 'var(--color-ink)' }}
            >
              {formatDayLong(day)}
            </button>
            <div className="flex flex-1 flex-col gap-1 p-2">
              {items.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-1 py-6 text-center text-xs" style={{ color: 'var(--color-muted)' }}>
                  <CalendarDays className="size-5" />
                  Keine Termine
                </div>
              ) : (
                items.map((o) => (
                  <button
                    key={o.id + o.occurrenceStart}
                    onClick={() => onEventClick(o)}
                    className="flex items-start gap-2 rounded-lg border-l-4 px-2 py-1.5 text-left text-sm hover:brightness-95"
                    style={{ backgroundColor: colorFor(o) + '18', borderColor: colorFor(o) }}
                  >
                    <div className="min-w-14 text-xs" style={{ color: 'var(--color-muted)' }}>
                      {o.allDay ? 'ganztägig' : isMultiDay(o) ? `${formatTime(o.occurrenceStart)} →` : formatTime(o.occurrenceStart)}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium" style={{ color: 'var(--color-ink)' }}>
                        {o.title}
                      </div>
                      {o.location && <div className="truncate text-xs" style={{ color: 'var(--color-muted)' }}>📍 {o.location}</div>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
